
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, CheckCircle, XCircle, RotateCcw } from 'lucide-react';

interface DocumentVerificationActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: 'approve' | 'reject' | 'reset' | null;
  username: string;
  currentStatus: string;
  onConfirm: (reason?: string) => Promise<void>;
  isProcessing: boolean;
}

const DocumentVerificationActionModal: React.FC<DocumentVerificationActionModalProps> = ({
  isOpen,
  onClose,
  action,
  username,
  currentStatus,
  onConfirm,
  isProcessing
}) => {
  const [reason, setReason] = useState('');
  const [showReasonField, setShowReasonField] = useState(false);

  useEffect(() => {
    if (action === 'reject') {
      setShowReasonField(true);
    } else {
      setShowReasonField(false);
      setReason('');
    }
  }, [action]);

  const getActionConfig = () => {
    if (!action) return null;

    const configs = {
      approve: {
        title: currentStatus === 'rejected' ? 'Reverter para Aprovado' : 'Aprovar Verificação',
        description: currentStatus === 'rejected' 
          ? 'Isso irá reverter a rejeição e aprovar os documentos'
          : 'Isso irá aprovar os documentos para verificação',
        icon: CheckCircle,
        color: 'text-green-400',
        buttonText: currentStatus === 'rejected' ? 'Reverter e Aprovar' : 'Aprovar',
        buttonClass: 'bg-green-600 hover:bg-green-700'
      },
      reject: {
        title: currentStatus === 'approved' ? 'Reverter para Rejeitado' : 'Rejeitar Verificação',
        description: currentStatus === 'approved'
          ? 'Isso irá reverter a aprovação e rejeitar os documentos'
          : 'Isso irá rejeitar os documentos. Um motivo é obrigatório.',
        icon: XCircle,
        color: 'text-red-400',
        buttonText: currentStatus === 'approved' ? 'Reverter e Rejeitar' : 'Rejeitar',
        buttonClass: 'bg-red-600 hover:bg-red-700'
      },
      reset: {
        title: 'Resetar Status',
        description: 'Isso irá voltar o status para "pendente", permitindo que o usuário reenvie documentos',
        icon: RotateCcw,
        color: 'text-yellow-400',
        buttonText: 'Resetar',
        buttonClass: 'bg-yellow-600 hover:bg-yellow-700'
      }
    };

    return configs[action];
  };

  const actionConfig = getActionConfig();
  if (!actionConfig) return null;

  const handleConfirm = async () => {
    if (action === 'reject' && !reason.trim()) {
      return; // Reason is required for reject
    }
    
    await onConfirm(reason.trim() || undefined);
  };

  const isReversalAction = 
    (action === 'approve' && currentStatus === 'rejected') ||
    (action === 'reject' && currentStatus === 'approved');

  const ActionIcon = actionConfig.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-black/95 border-white/20 text-white">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full bg-white/10 flex items-center justify-center ${actionConfig.color}`}>
              <ActionIcon className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-white">
                {actionConfig.title}
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Usuário: @{username}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-white/80 text-sm">
            {actionConfig.description}
          </p>

          {isReversalAction && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-yellow-400 font-medium text-sm">Atenção: Reversão de Status</p>
                  <p className="text-white/70 text-xs">
                    Esta ação irá reverter uma decisão administrativa anterior.
                  </p>
                </div>
              </div>
            </div>
          )}

          {showReasonField && (
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-white">
                Motivo da Rejeição *
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Descreva o motivo da rejeição..."
                className="bg-white/5 border-white/20 text-white placeholder:text-white/60"
                rows={3}
              />
              {action === 'reject' && !reason.trim() && (
                <p className="text-red-400 text-xs">
                  O motivo da rejeição é obrigatório
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            className="border-white/20 text-foreground hover:bg-white/10"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isProcessing || (action === 'reject' && !reason.trim())}
            className={`text-white ${actionConfig.buttonClass}`}
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Processando...
              </>
            ) : (
              actionConfig.buttonText
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentVerificationActionModal;
